import { Component, OnInit } from '@angular/core';
import { UserService } from '../../../api/services/user.service';
import { AuthService } from '../../../api/services/auth.service';
import { User, UserInfo } from '../../../api/models/user-model';
import { Delegation } from '../../../api/models/delegation';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { Approval } from '../../../api/models/approval';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { ApprovalService } from '../../../api/services/approval.service';
import { ToastrService } from '../../../../node_modules/ngx-toastr';
import { HeaderService } from '../../header/header.service';

@Component({
  selector: 'app-delegations',
  templateUrl: './delegations.component.html',
  styleUrls: ['./delegations.component.scss']
})
export class DelegationsComponent implements OnInit {

  constructor(private userService: UserService,
    private authService: AuthService,
    private approvalService: ApprovalService,
    private toastr: ToastrService,
    private headerService: HeaderService
  ) { }

  users: User[];
  delegationsUserIds: string[] = [];
  delegationUserIdsMap: { [key: string]: boolean } = {};
  userInfo: UserInfo[] = [];
  usersMap: { [key: string]: number } = {};
  currentUserId: string;
  delegation = new Delegation();
  lastTo: string;
  lastFrom: string;
  dateIncorrect = false;

  startDate = null;
  endDate = null;

  pendingDelegations: Delegation[] = [];
  archivedDelegations: Delegation[] = [];
  currentDelegations: Delegation[] = [];
  upcomingDelegations: Delegation[] = [];

  pendingToShow: Delegation[];
  archivedToShow: Delegation[];
  currentToShow: Delegation[];
  upcomingToShow: Delegation[];

  pendingApprovalsByCurrentUser: Approval[] = [];

  delegationsFiltered: any[];

  loaded = false;

  pageSize = 5;
  currentPage = 1;
  upcomingPage = 1;
  pendingPage = 1;
  archivedPage = 1;

  canDelegationBeApprovedByCurrentUser(delegationId: string) {
    return !!this.pendingApprovalsByCurrentUser.filter(a => this.getObjectId(a.objectID) === delegationId).length;
  }

  getObjectTypeCode(objectid: string) {
    return objectid.split('~')[0];
  }

  getObjectId(objectid: string) {
    return objectid.split('~')[1];
  }

  approve(approvalId: string) {
    this.approvalService.approveV2(approvalId).subscribe(
      success => {
        this.authService.getDelegations().subscribe(delegations => {
          this.regroupDelegations(delegations);
          this.onSuccess('Delegation approved');
        }, () => this.onError('Failed! Delegation not accepted!'));
      }, () => this.onError('Failed! Delegation not accepted!'));
  }

  deny(approvalId: string) {
    this.approvalService.deny(approvalId).subscribe(
      success => {
        this.authService.getDelegations().subscribe(delegations => {
          this.pendingDelegations = delegations.filter(d => d.status === 'CREATED' || d.status === 'PENDING_APPROVAL');
          this.loadPage(this.pendingPage, 'Pending');
          this.onSuccess('Delegation denied');
        }, () => this.onError('Failed! Delegation not declined!'));
      }, () => this.onError('Failed! Delegation not declined!'));
  }

  loadPage(page: number, delegationType: string) {
    const from = (page - 1) * this.pageSize;
    const to = from + this.pageSize;
    const lowerCaseDelegation = delegationType.toLocaleLowerCase();

    this[`${lowerCaseDelegation}ToShow`] = this[`${lowerCaseDelegation}Delegations`].slice(from, to);
  }

  delegationIndex(index: number, page: number): number {
    return (index + 1) + (this.pageSize * (page - 1));
  }

  createDelegation() {
    this.delegation['Load default Role Assignments'] = true;
    this.authService.createDelegation(this.delegation).subscribe(
      newDelegation => {
        this.pendingDelegations.unshift(newDelegation);
        this.approvalService.getPendingApprovalsByUser(this.currentUserId)
          .subscribe(approvals => {
            this.pendingApprovalsByCurrentUser = approvals.filter(a => this.getObjectTypeCode(a.objectID) === 'DELEGATION');
          });
        this.loadPage(this.pendingPage, 'Pending');
        this.headerService.refreshNotificationsBadge();
        this.onSuccess('Delegation created');
      }, () => {
        this.onError('Required data is missing!');
      }
    );
    this.lastFrom = this.delegation['Delegated From AuthUser Id'];
    this.lastTo = this.delegation['Delegated To AuthUser Id'];
    this.delegation = new Delegation();
    this.delegation['Delegated From AuthUser Id'] = this.lastFrom;
    this.delegation['Delegated To AuthUser Id'] = this.lastTo;
    this.startDate = null;
    this.endDate = null;
  }

  private onSuccess(successMessage: string) {
    this.toastr.success(successMessage, 'Success!', {
      timeOut: 1000,
      positionClass: 'toast-bottom-right'
    });
  }

  private onError(errorMessage: string) {
    this.toastr.error(errorMessage, 'Error!', {
      timeOut: 5000,
      positionClass: 'toast-bottom-right'
    });
  }

  setFrom(fromId: string) {
    this.delegation['Delegated From AuthUser Id'] = fromId;
  }

  setTo(toId: any) {
    this.delegation['Delegated To AuthUser Id'] = toId;
  }

  onStartDateSelect(date: NgbDateStruct) {
    this.delegation['Start Date'] = this.formatDate(date);
  }

  onEndDateSelect(date: NgbDateStruct) {
    this.delegation['End Date'] = this.formatDate(date);
    if (this.delegation['Start Date']) {
      this.dateIncorrect = this.delegation['Start Date'] >= this.delegation['End Date'];
    }
  }

  formatDate(date: NgbDateStruct): string {
    return new Date(date.year, date.month - 1, date.day).toISOString().slice(0, 10);
  }

  groupDelegationsByDate(delegations: Delegation[]) {
    const currentDate = new Date().toISOString().slice(0, 10);
    delegations.forEach(delegation => {
      if (currentDate >= delegation['Start Date'] && currentDate < delegation['End Date']) {
        this.currentDelegations.push(delegation);
      } else if (delegation['Start Date'] > currentDate) {
        this.upcomingDelegations.push(delegation);
      } else if (delegation['End Date'] < currentDate) {
        this.archivedDelegations.push(delegation);
      }
    });
    this.loadPage(this.currentPage, 'Current');
    this.loadPage(this.upcomingPage, 'Upcoming');
    this.loadPage(this.archivedPage, 'Archived');
  }

  regroupDelegations(delegations: Delegation[]) {
    this.pendingDelegations = delegations.filter(d => d.status === 'CREATED' || d.status === 'PENDING_APPROVAL');
    this.groupDelegationsByDate(delegations.filter(d => d.status === 'APPROVED'));
    this.loadPage(this.pendingPage, 'Pending');
  }

  getDelegationsUserIds(delegations: Delegation[]) {
    delegations.forEach(delegation => {
      const firstDelegation = delegation["Created By AuthUser Id"];
      const secondDelegation = delegation["Delegated From AuthUser Id"];
      const thirdDelegation = delegation["Delegated To AuthUser Id"];

      !this.delegationUserIdsMap[firstDelegation] && this.delegationsUserIds.push(firstDelegation);
      this.delegationUserIdsMap[firstDelegation] = true;

      !this.delegationUserIdsMap[secondDelegation] && this.delegationsUserIds.push(secondDelegation);
      this.delegationUserIdsMap[secondDelegation] = true;

      !this.delegationUserIdsMap[thirdDelegation] && this.delegationsUserIds.push(thirdDelegation);
      this.delegationUserIdsMap[thirdDelegation] = true;
    });
    this.userService.getUserDisplayNames(this.delegationsUserIds).subscribe(result => {
      this.userInfo = result;
      result.forEach(({ userId }, index) => {
        this.usersMap[userId] = index;
      });
    });
  }

  getName(userId: string) {
    const info = userId in this.usersMap && this.userInfo[this.usersMap[userId]];

    return info ? info.displayName : 'N/A';
  }

  ngOnInit() {
    this.userService.findUserById(this.authService.getUserId()).subscribe(user => {
      this.currentUserId = user['User ID'];
      this.delegation['Delegated From AuthUser Id'] = this.currentUserId;
      this.userService.getUsersFilteringByOrgId(user['Organization ID']).subscribe(users => {
        this.users = users;
        forkJoin(this.authService.getDelegations(), this.approvalService.getPendingApprovalsByUser(this.currentUserId))
          .subscribe((delegationsAndApprovals: [Delegation[], Approval[]]) => {
            const delegations = delegationsAndApprovals[0];
            const approvals = delegationsAndApprovals[1];
            this.getDelegationsUserIds(delegations);
            this.regroupDelegations(delegations);
            this.pendingApprovalsByCurrentUser = approvals.filter(a => this.getObjectTypeCode(a.objectID) === 'DELEGATION');
            this.loaded = true;
          });
      });
    });

  }

}



// WEBPACK FOOTER //
// /codebuild/output/src3408204168/src/bitbucket.org/tesscotds/webclient/src/app/management/delegations/delegations.component.ts